/**
 * VerseFavoritesDropdown Component
 *
 * Dropdown in the reader header that shows all favorited verses grouped by parsha.
 * Allows jumping to verses within the current parsha or opening a different parsha.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Heart, ChevronRight, BookOpen } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import type { FavoriteItem } from '@/types/favorites';

interface VerseFavoritesDropdownProps {
  currentParshaRef: string;
  onJumpToVerse: (verseRef: string) => void;
  onOpenDifferentParsha: (
    parshaRef: string,
    parshaTitle: string,
    parshaHebrew: string,
    focusVerseRef: string
  ) => void;
}

interface GroupedVerses {
  parshaContext: string;
  parshaRef?: string;
  parshaHebrew?: string;
  verses: FavoriteItem[];
}

/**
 * Extract parsha title from context (e.g., "Parashat Beshalach" -> "Beshalach")
 */
function extractParshaTitle(parshaContext?: string): string {
  if (!parshaContext) return 'Unknown Parsha';
  return (
    parshaContext
      .replace(/^Parashat\s+/i, '')
      .replace(/^Parsha\s+/i, '')
      .trim() || 'Unknown Parsha'
  );
}

/**
 * Check if a verse belongs to the current parsha
 */
function isVerseInCurrentParsha(verse: FavoriteItem, currentParshaRef: string): boolean {
  // If we have the parshaRef stored, compare directly
  if (verse.cachedData.parshaRef) {
    return verse.cachedData.parshaRef === currentParshaRef;
  }

  // Fallback: try to match by extracting book from verse ref and parsha ref
  const verseBook = verse.itemId.split(' ')[0];
  const parshaBook = currentParshaRef.split('.')[0];
  return verseBook.toLowerCase() === parshaBook.toLowerCase();
}

export function VerseFavoritesDropdown({
  currentParshaRef,
  onJumpToVerse,
  onOpenDifferentParsha,
}: VerseFavoritesDropdownProps) {
  const { getFilteredFavorites } = useFavorites();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all verse favorites
  const verseFavorites = useMemo(() => {
    return getFilteredFavorites('verse');
  }, [getFilteredFavorites]);

  // Group verses by parsha
  const groupedVerses = useMemo(() => {
    const groups: Record<string, GroupedVerses> = {};

    verseFavorites.forEach((verse) => {
      const context = verse.cachedData.parshaContext || 'Other';

      if (!groups[context]) {
        groups[context] = {
          parshaContext: context,
          parshaRef: verse.cachedData.parshaRef,
          parshaHebrew: verse.cachedData.parshaHebrew,
          verses: [],
        };
      }
      groups[context].verses.push(verse);
    });

    // Sort groups: current parsha first, then alphabetically
    return Object.values(groups).sort((a, b) => {
      const aIsCurrent = a.parshaRef === currentParshaRef;
      const bIsCurrent = b.parshaRef === currentParshaRef;

      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return a.parshaContext.localeCompare(b.parshaContext);
    });
  }, [verseFavorites, currentParshaRef]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleVerseClick = (verse: FavoriteItem) => {
    const isInCurrentParsha = isVerseInCurrentParsha(verse, currentParshaRef);

    if (isInCurrentParsha) {
      // Jump to verse within current parsha
      onJumpToVerse(verse.itemId);
    } else {
      // Open different parsha with focus on this verse
      const parshaRef = verse.cachedData.parshaRef || '';
      const parshaTitle = extractParshaTitle(verse.cachedData.parshaContext);
      const parshaHebrew = verse.cachedData.parshaHebrew || '';
      onOpenDifferentParsha(parshaRef, parshaTitle, parshaHebrew, verse.itemId);
    }

    setIsOpen(false);
  };

  const totalCount = verseFavorites.length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          p-2 rounded-lg transition-colors relative
          ${isOpen ? 'bg-[#8e4585]' : 'hover:bg-[#8e4585]'}
        `}
        aria-label="Verse favorites"
        aria-expanded={isOpen}
      >
        <Heart size={20} className={totalCount > 0 ? 'fill-current' : ''} />

        {/* Count badge */}
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {/* Dropdown panel - fixed positioned and centered (matching AudioPlayerDropdown) */}
      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div className="fixed inset-0 z-[110]" onClick={() => setIsOpen(false)} />

          {/* Dropdown - Fixed positioned and centered like AudioPlayerDropdown */}
          <div className="fixed left-1/2 -translate-x-1/2 top-[60px] w-[calc(100%-2rem)] max-w-sm bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[120] max-h-[60vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex-shrink-0">
              <h3 className="font-bold text-slate-800">Favorite Verses</h3>
              <p className="text-xs text-slate-500">
                {totalCount} {totalCount === 1 ? 'verse' : 'verses'} saved
              </p>
            </div>

            {/* Content */}
            {totalCount === 0 ? (
              <div className="p-6 text-center text-slate-400">
                <Heart size={32} className="mx-auto mb-2 opacity-50" />
                <p>No favorite verses yet</p>
                <p className="text-xs mt-1">Tap the heart icon on any verse to save it</p>
              </div>
            ) : (
              <div className="py-2 overflow-y-auto flex-1">
                {groupedVerses.map((group) => {
                  const isCurrentParsha = group.parshaRef === currentParshaRef;

                  return (
                    <div key={group.parshaContext} className="mb-2">
                      {/* Parsha group header */}
                      <div
                        className={`
                          px-4 py-2 flex items-center gap-2
                          ${isCurrentParsha ? 'bg-purple-50' : 'bg-slate-50'}
                        `}
                      >
                        <BookOpen
                          size={14}
                          className={isCurrentParsha ? 'text-[#8e4585]' : 'text-slate-400'}
                        />
                        <span
                          className={`
                            text-xs font-semibold uppercase tracking-wide
                            ${isCurrentParsha ? 'text-[#8e4585]' : 'text-slate-500'}
                          `}
                        >
                          {extractParshaTitle(group.parshaContext)}
                          {isCurrentParsha && ' (Current)'}
                        </span>
                      </div>

                      {/* Verses in this group */}
                      {group.verses.map((verse) => (
                        <button
                          key={verse.id}
                          onClick={() => handleVerseClick(verse)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-start gap-3 group"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 text-sm">
                              {verse.cachedData.title}
                            </div>
                            {verse.cachedData.preview && (
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                                {verse.cachedData.preview}
                              </p>
                            )}
                          </div>
                          <ChevronRight
                            size={16}
                            className="text-slate-300 group-hover:text-[#8e4585] transition-colors mt-0.5 shrink-0"
                          />
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
